import styles from './CalendarModuleNewEventButton.scss';
import { getFirstFocusable } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react-lite';
import { preventFocusDuringCurrentRender } from 'owa-calendar-focus-manager';
import isFeatureEnabled from 'owa-feature-flags/lib/utils/isFeatureEnabled';
import FluentCommandBarHeroButton from 'owa-fluent-commandbar/lib/FluentCommandBarHeroButton';
import CalendarAdd from 'owa-fluent-icons-svg/lib/icons/CalendarAddRegular';
import Mail from 'owa-fluent-icons-svg/lib/icons/MailRegular';
import loc from 'owa-localize';
import { useOrchestrator } from 'owa-react-hooks/lib/useOrchestrator';
import SkipLinkRegionToFirstFocusableChild from 'owa-skip-link/lib/components/SkipLinkRegionToFirstFocusableChild';
import * as React from 'react';
import {
    newCalendarEventText,
    newEventRegionName,
    newCalendarEventMenuItemButtonText,
    newMailMenuItemButtonText,
} from './CalendarModuleNewEventButton.locstring.json';
import {
    focusNewEventButton,
    invokeNewEventByKeyboard,
    launchNewEvent,
    launchNewMail,
} from 'owa-calendar-module-commanding';
import {
    NEW_BUTTON_ANCHOR_NAME,
    NEW_BUTTON_ENTRY_SOURCE,
    KEYBOARD_SHORTCUT_ENTRY_SOURCE,
} from 'owa-calendar-meeting-forms-common-store/lib/helpers/newButtonConstants';
import type {
    QuickComposeEntrySourceType,
    FullComposeEntrySourceType,
} from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import type { IContextualMenuItem, IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';

export default observer(function CalendarModuleNewEventButton() {
    const isSplitButton = isFeatureEnabled('mon-ribbon');

    const onClickNewEvent = React.useCallback(() => {
        invokeNewEvent(NEW_BUTTON_ENTRY_SOURCE);
    }, []);

    useOrchestrator(
        invokeNewEventByKeyboard,
        () => {
            invokeNewEvent(KEYBOARD_SHORTCUT_ENTRY_SOURCE);
        },
        []
    );

    useOrchestrator(
        focusNewEventButton,
        () => {
            const newEventButtonAnchor = getNewEventButtonAnchor();
            if (newEventButtonAnchor) {
                // wait for the next animation frame so that any forms (e.g. compose) have actually closed before setting focus
                preventFocusDuringCurrentRender();
                setTimeout(() => {
                    newEventButtonAnchor?.focus();
                }, 0);
            }
        },
        []
    );

    return (
        <SkipLinkRegionToFirstFocusableChild
            role="complementary"
            regionNameResourceId={newEventRegionName}>
            <FluentCommandBarHeroButton
                text={loc(newCalendarEventText)}
                id={NEW_BUTTON_ANCHOR_NAME}
                onClick={onClickNewEvent}
                menuProps={isSplitButton ? getMenuProps() : null}
                iconProps={
                    isSplitButton
                        ? {
                              iconName: CalendarAdd,
                              styles: {
                                  root: styles.newEventButtonIcon,
                              },
                          }
                        : null
                }
            />
        </SkipLinkRegionToFirstFocusableChild>
    );
});

function invokeNewEvent(entrySource: QuickComposeEntrySourceType & FullComposeEntrySourceType) {
    launchNewEvent(entrySource, getNewEventButtonAnchor());
}

function getNewEventButtonAnchor() {
    const newEventButtonElem = document.getElementById(NEW_BUTTON_ANCHOR_NAME);
    return getFirstFocusable(
        newEventButtonElem,
        newEventButtonElem,
        true /* includeElementsInFocusZones */
    ) as HTMLDivElement;
}

function invokeNewMail() {
    launchNewMail('CommandBar');
}

function getMenuProps(): IContextualMenuProps {
    const menuProps: IContextualMenuProps = { items: [] };

    // New message menu item
    const newMessageMenuItem: IContextualMenuItem = {
        key: 'newMessage',
        text: loc(newMailMenuItemButtonText),
        iconProps: { iconName: Mail },
        onClick: () => invokeNewMail(),
    };

    menuProps.items.push(newMessageMenuItem);

    // New event menu item
    const newEventMenuItem: IContextualMenuItem = {
        key: 'newEvent',
        text: loc(newCalendarEventMenuItemButtonText),
        iconProps: { iconName: CalendarAdd },
        onClick: () => invokeNewEvent(NEW_BUTTON_ENTRY_SOURCE),
    };

    menuProps.items.push(newEventMenuItem);

    return menuProps;
}
